import { useTheme } from "@deere/fuel-react";
import { Box, Typography } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { useContext, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { fetchMyLocatorInventory } from "./apiUtils";
import MyLocatorInventoryTable from "./components/MyLocatorInventoryTable/MyLocatorInventoryTable";
import {
  onFirstPageClick,
  onLastPageClick,
  onNextClick,
  onPreviousPageClick,
  onRowsPerPageChange,
} from "./paginationUtils";
import { setSortModelAction } from "./reducers/actions";
import {
  getInitialState,
  myLocatorInventoryReducer,
} from "./reducers/reducers";
import Pagination from "../../../components/DataGridPro/Pagination/Pagination";
import StickyFooter from "../../../components/StickyFooter/StickyFooter";
import { ConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";

const MyLocatorInventory = () => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);
  const { authState } = useOktaAuth();
  const { dealerAccount } = useContext(DealerAccountContext);
  const { D2D_INVENTORY_API_URL } = useContext(ConfigContext);

  const [state, dispatch] = useReducer(
    myLocatorInventoryReducer(),
    getInitialState(dealerAccount?.id || "")
  );

  useEffect(() => {
    if (!authState?.isAuthenticated) return;

    fetchMyLocatorInventory(
      D2D_INVENTORY_API_URL,
      state,
      authState.accessToken?.accessToken || "",
      dispatch
    );
  }, [authState?.isAuthenticated, dealerAccount?.id, D2D_INVENTORY_API_URL]);

  useEffect(() => {
    if (state.allInventoryItems.length === 0) return;

    let processedData = [...state.allInventoryItems];

    if (state.filters.partNumber) {
      const searchTerm = state.filters.partNumber.toLowerCase();
      processedData = processedData.filter((item) =>
        item.partNumber.toLowerCase().includes(searchTerm)
      );
    }

    if (state.filters.quantity) {
      processedData = processedData.filter((item) =>
        item.quantity.toString().includes(state.filters.quantity!)
      );
    }

    if (state.filters.dateTimeUpdatedFrom || state.filters.dateTimeUpdatedTo) {
      processedData = processedData.filter((item) => {
        const itemDate = new Date(item.dateTimeUpdated);
        let isInRange = true;

        if (state.filters.dateTimeUpdatedFrom) {
          isInRange =
            isInRange && itemDate >= state.filters.dateTimeUpdatedFrom.toDate();
        }

        if (state.filters.dateTimeUpdatedTo) {
          isInRange =
            isInRange &&
            itemDate <= state.filters.dateTimeUpdatedTo.endOf("day").toDate();
        }

        return isInRange;
      });
    }

    if (state.sortModel.length > 0) {
      const { field, sort } = state.sortModel[0];
      processedData.sort((a, b) => {
        const aValue = a[field as keyof typeof a];
        const bValue = b[field as keyof typeof b];

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sort === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    const startIndex = (state.currentPage - 1) * state.pageRowSize;
    const endIndex = startIndex + state.pageRowSize;
    const paginatedData = processedData.slice(startIndex, endIndex);

    dispatch({
      type: "SET_INVENTORY_ITEMS" as any,
      payload: {
        inventoryItems: paginatedData,
        totalInventoryItems: processedData.length,
      },
    });
  }, [
    state.allInventoryItems,
    state.currentPage,
    state.pageRowSize,
    state.sortModel,
    state.filters,
    dispatch,
  ]);

  const showPagination =
    state.totalInventoryItems > state.pageRowSize && !state.isTechnicalError;

  return (
    <>
      <Box>
        <Typography
          component="h1"
          data-fuel-size="3xl"
          fontWeight="semibold"
          sx={{
            paddingBottom: theme.tokens.FuelSpacing6,
            color: theme.tokens.FuelColorTextPrimary,
          }}
          data-testid="my-locator-inventory-header"
        >
          {t("d2d:myLocatorInventory.header.title", "My Locator Inventory")}
        </Typography>

        <MyLocatorInventoryTable
          inventory={{
            inventoryItems: state.inventoryItems,
            totalInventoryItems: state.totalInventoryItems,
          }}
          loading={state.isLoading}
          showAuthenticationError={state.isAuthenticationError}
          showTechnicalError={state.isTechnicalError}
          sort={{
            sortModel: state.sortModel,
            updateSortModel: (model) => dispatch(setSortModelAction(model)),
          }}
          filter={{
            myLocatorInventoryFilters: state.filters,
            isFilterApplied: state.isFilterApplied,
          }}
          dispatch={dispatch}
        />
      </Box>

      {showPagination && (
        <StickyFooter>
          <Pagination
            rowsPerPage={state.pageRowSize}
            currentPage={state.currentPage}
            totalCount={state.totalInventoryItems}
            onRowsPerPageChange={(updatedRowsPerPage: number) =>
              onRowsPerPageChange(dispatch, updatedRowsPerPage)
            }
            onFirstPageClick={() => onFirstPageClick(dispatch)}
            onPreviousClick={() =>
              onPreviousPageClick(dispatch, state.currentPage)
            }
            onLastPageClick={() =>
              onLastPageClick(
                dispatch,
                state.pageRowSize,
                state.totalInventoryItems
              )
            }
            onNextClick={() => onNextClick(dispatch, state.currentPage)}
          />
        </StickyFooter>
      )}
    </>
  );
};

export default MyLocatorInventory;
